import React, { useMemo, useRef, useState, useEffect } from "react";
import { usePlayer } from "../../../../Contexts/playerData.context";
import { getUserFromToken } from "../../../../utils/functions.ts";

const ENDPOINT = "https://www.fbxchange.com/ReactPHP/tomBox.php";

const PlayerMerge = () => {
  const { playerData, setPlayerData } = usePlayer();

  const [activeBox, setActiveBox] = useState("left");
  const [leftId, setLeftId] = useState("");
  const [rightId, setRightId] = useState("");

  const [leftData, setLeftData] = useState(null);
  const [rightData, setRightData] = useState(null);

  const [leftLoading, setLeftLoading] = useState(false);
  const [rightLoading, setRightLoading] = useState(false);

  const [leftError, setLeftError] = useState("");
  const [rightError, setRightError] = useState("");

  const [transferMade, setTransferMade] = useState(null); // 'Left' | 'Right' | null
  const [completing, setCompleting] = useState(false);

  const leftRef = useRef(null);
  const rightRef = useRef(null);

  const getId = (p) => p?.FBXID ?? p?.fbxid ?? p?.id ?? "";
  const getLabel = (p) => {
    const last = p?.LAST ?? p?.last ?? "";
    const first = p?.FIRST ?? p?.first ?? "";
    const school = p?.SCHOOL ?? p?.school ?? "";
    const pos = p?.COL_POS1 ?? p?.colPos ?? "";
    const id = getId(p);
    const name = last || first ? `${last}${last && first ? ", " : ""}${first}` : String(id);
    return `${name}${school ? ` — ${school}` : ""}${pos ? ` - ${pos}` : ""} (${id})`;
  };

  const options = useMemo(
    () =>
      (playerData || [])
        .map((p) => ({ id: String(getId(p)), label: getLabel(p) }))
        .filter((o) => o.id !== ""),
    [playerData]
  );

  const focusLeft = () => { setActiveBox("left"); leftRef.current?.focus(); };
  const focusRight = () => { setActiveBox("right"); rightRef.current?.focus(); };

  const fetchTomBoxData = async (id) => {
    const user = await getUserFromToken();
    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ FBXID: id, script: "getSNPQT", user }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  };

  // Helper: re-fetch current sides (used by Cancel and after Complete Merge)
  const refetchSides = async () => {
    try {
      if (leftId) {
        setLeftLoading(true);
        const d = await fetchTomBoxData(leftId);
        setLeftData(d);
      } else {
        setLeftData(null);
      }
    } catch (e) {
      setLeftError(e?.message || "Failed to load");
      setLeftData(null);
    } finally {
      setLeftLoading(false);
    }

    try {
      if (rightId) {
        setRightLoading(true);
        const d = await fetchTomBoxData(rightId);
        setRightData(d);
      } else {
        setRightData(null);
      }
    } catch (e) {
      setRightError(e?.message || "Failed to load");
      setRightData(null);
    } finally {
      setRightLoading(false);
    }
  };

  // Cancel handler: reset merge and reload both sides
  const handleCancelMerge = async () => {
    setTransferMade(null);
    await refetchSides();
  };

  // COMPLETE MERGE — posts minimal payload, then refreshes UI (demo: remove dropped from context)
  const handleCompleteMerge = async () => {
    const keepId = transferMade === "Left" ? leftId : transferMade === "Right" ? rightId : "";
    const dropId = transferMade === "Left" ? rightId : transferMade === "Right" ? leftId : "";
    if (!keepId || !dropId) return;

    setCompleting(true);
    try {
       const response = await fetch('https://www.fbxchange.com/ReactPHP/tomBox.php', {
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          script: 'mergePlayers',
          loseDR: dropId,
          keepDR: keepId
        }),
      });
      const data = await response.json();
      if (!data || data.worked !== "OK") {
        throw new Error("Merge failed on server.");
      }

      setPlayerData((prev) =>
        Array.isArray(prev) ? prev.filter((p) => String(getId(p)) !== String(dropId)) : prev
      );

      // Reset the UI after completion
      setLeftId("");
      setRightId("");
      setLeftData(null);
      setRightData(null);
      setLeftError("");
      setRightError("");
      setTransferMade(null);
    } catch (e) {
      console.error("Complete Merge failed:", e);
      alert(e?.message || "Complete Merge failed.");
    } finally {
      setCompleting(false);
    }
  };

  useEffect(() => {
    let aborted = false;
    if (!leftId) {
      setLeftData(null); setLeftError(""); setLeftLoading(false);
      return;
    }
    setLeftLoading(true); setLeftError("");
    fetchTomBoxData(leftId)
      .then((data) => !aborted && setLeftData(data))
      .catch((err) => { if (!aborted) { setLeftData(null); setLeftError(err.message || "Failed to load"); } })
      .finally(() => !aborted && setLeftLoading(false));
    return () => { aborted = true; };
  }, [leftId]);

  useEffect(() => {
    let aborted = false;
    if (!rightId) {
      setRightData(null); setRightError(""); setRightLoading(false);
      return;
    }
    setRightLoading(true); setRightError("");
    fetchTomBoxData(rightId)
      .then((data) => !aborted && setRightData(data))
      .catch((err) => { if (!aborted) { setRightData(null); setRightError(err.message || "Failed to load"); } })
      .finally(() => !aborted && setRightLoading(false));
    return () => { aborted = true; };
  }, [rightId]);

  const renderStatsTable = (data) => {
    if (!data) return null;
    return (
      <table className="table table-sm mt-2">
        <tbody>
          <tr><td>DR Num</td><td>{data.drnum}</td></tr>
          <tr><td>Last Name</td><td>{data.LAST}</td></tr>
          <tr><td>First Name</td><td>{data.FIRST}</td></tr>
          <tr><td>Scout Reports</td><td>{data.scout}</td></tr>
          <tr><td>Note</td><td>{data.note}</td></tr>
          <tr><td>Transactions</td><td>{data.trans}</td></tr>
          <tr><td>Questionnaire</td><td>{data.quiz}</td></tr>
          <tr><td>PDF / Uploads</td><td>{data.pdf}</td></tr>
          <tr><td>Contact Form</td><td>{data.contact}</td></tr>
          <tr><td>Draft Scout Data</td><td>{data.draftscout}</td></tr>
        </tbody>
      </table>
    );
  };

  const handleKeepPlayer = (data, side) => {
    if (!data) return;

    // zero out counters on the opposite side
    const ZERO_COUNTERS = {
      scout: 0, note: 0, trans: 0, quiz: 0, pdf: 0, contact: 0, draftscout: 0,
    };

    if (side === "left" && rightData) {
      setLeftData((prev) => ({
        ...prev,
        scout: (Number(prev?.scout) || 0) + (Number(rightData.scout) || 0),
        note: (Number(prev?.note) || 0) + (Number(rightData.note) || 0),
        trans: (Number(prev?.trans) || 0) + (Number(rightData.trans) || 0),
        quiz: (Number(prev?.quiz) || 0) + (Number(rightData.quiz) || 0),
        pdf: (Number(prev?.pdf) || 0) + (Number(rightData.pdf) || 0),
        contact: (Number(prev?.contact) || 0) + (Number(rightData.contact) || 0),
        draftscout: (Number(prev?.draftscout) || 0) + (Number(rightData.draftscout) || 0),
      }));
      setRightData((prev) => (prev ? { ...prev, ...ZERO_COUNTERS } : prev));
      setTransferMade("Left");
    } else if (side === "right" && leftData) {
      setRightData((prev) => ({
        ...prev,
        scout: (Number(prev?.scout) || 0) + (Number(leftData.scout) || 0),
        note: (Number(prev?.note) || 0) + (Number(leftData.note) || 0),
        trans: (Number(prev?.trans) || 0) + (Number(leftData.trans) || 0),
        quiz: (Number(prev?.quiz) || 0) + (Number(leftData.quiz) || 0),
        pdf: (Number(prev?.pdf) || 0) + (Number(leftData.pdf) || 0),
        contact: (Number(prev?.contact) || 0) + (Number(leftData.contact) || 0),
        draftscout: (Number(prev?.draftscout) || 0) + (Number(leftData.draftscout) || 0),
      }));
      setLeftData((prev) => (prev ? { ...prev, ...ZERO_COUNTERS } : prev));
      setTransferMade("Right");
    }
  };

  // Derived UI flags
  const bothLoaded =
    !!leftData && !!rightData &&
    !leftLoading && !rightLoading &&
    !leftError && !rightError;

  const samePlayer =
    bothLoaded &&
    leftId && rightId &&
    String(leftId) === String(rightId);

  // Dynamic classes for left/right boxes
  const leftAlert =
    transferMade === "Left" ? "alert-success" :
      transferMade === "Right" ? "alert-danger" : "";
  const rightAlert =
    transferMade === "Right" ? "alert-success" :
      transferMade === "Left" ? "alert-danger" : "";

  return (
    <>
      {/* Global warning when both sides are the same player */}
      {samePlayer && (
        <div className="alert alert-danger p-3 text-center mb-4">
          Cannot Merge the Same Player
        </div>
      )}

      <div className="d-flex gap-2">
        {/* LEFT BOX */}
        <div
          className={`col-24 border rounded p-2 ${leftAlert} ${activeBox === "left" ? "border-primary shadow-sm" : "border-dark"}`}
          tabIndex={0}
          ref={leftRef}
          onClick={focusLeft}
          role="group"
        >
          <div className="fw-bold mb-2">Player 1</div>
          <select
            className="form-select fs-7"
            value={leftId}
            onChange={(e) => setLeftId(e.target.value)}
            onFocus={focusLeft}
          >
            <option value="">Choose Player from Dropdown…</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>

          {leftLoading && <small className="text-muted">Loading data…</small>}
          {leftError && <small className="text-danger">Error: {leftError}</small>}
          {!leftLoading && !leftError && leftData && renderStatsTable(leftData)}

          {/* Buttons: hide "Keep" until both loaded & not the same */}
          {transferMade === null ? (
            bothLoaded && !samePlayer ? (
              <button
                className="btn btn-sm btn-success text-white w-100 mt-2"
                title="Keep This Player"
                onClick={() => handleKeepPlayer(leftData, "left")}
              >
                Keep This Player
              </button>
            ) : null
          ) : transferMade === "Right" ? (
            // When Right was kept, LEFT shows Cancel
            <button
              className="btn btn-sm btn-outline-danger w-100 mt-2"
              onClick={handleCancelMerge}
              title="Cancel and reload original data"
              disabled={completing}
            >
              Cancel
            </button>
          ) : (
            // transferMade === "Left" → show Complete Merge on the LEFT side
            <button
              className="btn btn-sm btn-primary w-100 mt-2"
              onClick={handleCompleteMerge}
              disabled={completing}
              title="Finalize merge"
            >
              {completing ? "Completing…" : "Complete Merge"}
            </button>
          )}
        </div>

        {/* RIGHT BOX */}
        <div
          className={`col-24 border rounded p-2 ${rightAlert} ${activeBox === "right" ? "border-primary shadow-sm" : "border-dark"}`}
          tabIndex={0}
          ref={rightRef}
          onClick={focusRight}
          role="group"
        >
          <div className="fw-bold mb-2">Player 2</div>
          <select
            className="form-select fs-7"
            value={rightId}
            onChange={(e) => setRightId(e.target.value)}
            onFocus={focusRight}
          >
            <option value="">Choose Player from Dropdown…</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>

          {rightLoading && <small className="text-muted">Loading data…</small>}
          {rightError && <small className="text-danger">Error: {rightError}</small>}
          {!rightLoading && !rightError && rightData && renderStatsTable(rightData)}

          {/* Buttons: hide "Keep" until both loaded & not the same */}
          {transferMade === null ? (
            bothLoaded && !samePlayer ? (
              <button
                className="btn btn-sm btn-success text-white w-100 mt-2"
                title="Keep This Player"
                onClick={() => handleKeepPlayer(rightData, "right")}
              >
                Keep This Player
              </button>
            ) : null
          ) : transferMade === "Left" ? (
            // When Left was kept, RIGHT shows Cancel
            <button
              className="btn btn-sm btn-outline-danger w-100 mt-2"
              onClick={handleCancelMerge}
              title="Cancel and reload original data"
              disabled={completing}
            >
              Cancel
            </button>
          ) : (
            // transferMade === "Right" → show Complete Merge on the RIGHT side
            <button
              className="btn btn-sm btn-primary w-100 mt-2"
              onClick={handleCompleteMerge}
              disabled={completing}
              title="Finalize merge"
            >
              {completing ? "Completing…" : "Complete Merge"}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default PlayerMerge;
